import React, { useContext, useEffect, useRef, useState } from "react";
import useApi from "@/hooks/useApi";
import { SiteContext } from "@/context/SiteContext";
import { ShieldCheck } from "@/components/ds/icons";
import { PrimaryButton } from "@/components/ds";

interface PendingNotice {
  id: number;
  policyType: "Terms" | "Privacy" | "AUP";
  version: string;
  effectiveDate: string;
  summaryText: string;
  viewedAt: string | null;
}

const POLICY_LABELS: Record<string, string> = {
  Terms: "Terms of Service",
  Privacy: "Privacy Policy",
  AUP: "Acceptable Use Policy"
};

/**
 * Server-driven banner for the policy-acknowledgment system (velaro-marketing terms.html §16):
 * when Velaro materially changes Terms/Privacy/AUP, the designated Account Owner sees this
 * banner until they acknowledge it. Visibility is NOT sessionStorage-based like
 * SupportSessionBanner — it polls the server for the current site's pending notices, because
 * the notice must persist across sessions/devices until actually acknowledged.
 *
 * Multiple policy types can be pending at once (e.g. Terms AND Privacy both changed). We show
 * one at a time (queue), most-recently-created first, so the banner never gets visually
 * cluttered — each acknowledged notice reveals the next one, if any.
 *
 * Per terms.html wording ("views or acknowledges"), a passive view counts — the "viewed" log
 * fires automatically on mount for the currently-shown notice, no click required. The explicit
 * "Acknowledge" button is required only to fully dismiss the banner and log AcknowledgedAt.
 */
export default function PolicyAcknowledgmentBanner() {
  const api = useApi();
  const { site } = useContext(SiteContext);
  const [queue, setQueue] = useState<PendingNotice[]>([]);
  const [acking, setAcking] = useState(false);
  const viewedIds = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!site?.siteId) return;
    let cancelled = false;

    async function load() {
      try {
        const res = await api.admin.get("PolicyNotices/pending");
        if (!res.ok || cancelled) return;
        const data: PendingNotice[] = await res.json();
        setQueue(data);
      } catch {
        // Fails silently — the email notice already started the legal notice clock;
        // the banner is supplemental. Nothing to interrupt the admin UI for here.
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [api, site?.siteId]);

  const current = queue[0];

  useEffect(() => {
    if (!current || viewedIds.current.has(current.id)) return;
    viewedIds.current.add(current.id);
    api.admin.post(`PolicyNotices/${current.id}/viewed`, {}).catch(() => {});
  }, [current, api]);

  if (!current) return null;

  async function acknowledge() {
    if (!current) return;
    setAcking(true);
    try {
      await api.admin.post(`PolicyNotices/${current.id}/acknowledge`, {});
    } catch {
      // If the acknowledge call fails, keep the banner up so the admin can retry — do not
      // silently drop it from the queue on a network error.
      setAcking(false);
      return;
    }
    setQueue((q) => q.slice(1));
    setAcking(false);
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-[#0d1535] text-white px-4 py-3 shadow-lg">
      <div className="max-w-5xl mx-auto flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5 text-[#3383EC]" />
          <div>
            <p className="text-sm font-semibold text-white">
              Updated {POLICY_LABELS[current.policyType] ?? current.policyType}
              <span className="text-zinc-300 font-normal">
                {" "}
                — effective{" "}
                {new Date(current.effectiveDate).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })}
              </span>
            </p>
            <p className="text-xs text-zinc-300 mt-1 max-w-2xl">
              {current.summaryText}
            </p>
          </div>
        </div>
        <PrimaryButton
          onClick={acknowledge}
          disabled={acking}
          className="shrink-0 !bg-white !text-[#0d1535] !rounded-lg !px-4 !py-2 !text-xs hover:!bg-zinc-100 hover:!opacity-100"
        >
          {acking ? "Acknowledging…" : "Acknowledge"}
        </PrimaryButton>
      </div>
    </div>
  );
}
